'use client'

import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { track } from '@/utils/analytics'
import AppStoreLink from '@/app/components/AppStoreLink'
import type { SearchRecord } from './_data/products'
import type { CategorySlug } from './_data/types'

/**
 * The free lookup. This is the top of the funnel the site did not have: a
 * Google visitor lands on an article, follows one link, and gets an answer
 * about a real product without installing anything.
 *
 * The whole dataset ships as a narrow SearchRecord[] (slug, name, score, band,
 * grade, a lowercased haystack) rather than the full prose — around 8 KB for
 * fifty products instead of ninety. Matching is a substring scan over that
 * haystack, which is instant at this size and needs no index library. If the
 * database reaches four figures this becomes a server route; it is not close.
 *
 * `product_search_miss` is the event that matters commercially. Every miss is
 * a product a real person wanted scored, which is the queue for what we add
 * next — the users write the roadmap by searching.
 */

interface Props {
  records: SearchRecord[]
  categories: { slug: CategorySlug; name: string }[]
}

type SortKey = 'worst' | 'best' | 'name'

/** Rows shown before the visitor asks for more. Searching or filtering shows
 *  everything that matched — this cap only applies to the idle browse view. */
const INITIAL_ROWS = 30

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'worst', label: 'Highest exposure' },
  { key: 'best', label: 'Lowest exposure' },
  { key: 'name', label: 'A–Z' },
]

export default function ProductExplorer({ records, categories }: Props) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<CategorySlug | 'all'>('all')
  const [sort, setSort] = useState<SortKey>('worst')
  // The unfiltered list is 100+ rows. Showing all of them before the visitor
  // has expressed any intent buries the search box and the sections below it.
  const [expanded, setExpanded] = useState(false)

  // useDeferredValue keeps typing responsive while the list re-filters. At 50
  // records it is not strictly needed; it costs nothing and means the
  // component does not need rewriting when the database grows.
  const deferredQuery = useDeferredValue(query)

  const results = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase()
    const terms = q.split(/\s+/).filter(Boolean)

    const filtered = records.filter((r) => {
      if (category !== 'all' && r.category !== category) return false
      if (!terms.length) return true
      return terms.every((t) => r.terms.includes(t))
    })

    if (sort === 'name') return [...filtered].sort((a, b) => a.name.localeCompare(b.name))
    if (sort === 'best') return [...filtered].sort((a, b) => a.score - b.score)
    return [...filtered].sort((a, b) => b.score - a.score)
  }, [records, deferredQuery, category, sort])

  // Fire one event per settled query rather than one per keystroke.
  const lastTracked = useRef('')
  useEffect(() => {
    const q = query.trim()
    if (q.length < 3) return
    const timer = setTimeout(() => {
      if (lastTracked.current === q.toLowerCase()) return
      lastTracked.current = q.toLowerCase()
      const hits = records.filter((r) =>
        q
          .toLowerCase()
          .split(/\s+/)
          .filter(Boolean)
          .every((t) => r.terms.includes(t))
      ).length
      track(hits > 0 ? 'product_search' : 'product_search_miss', {
        cta_surface: 'product_explorer',
        page_type: 'product_database',
        search_query: q.toLowerCase().slice(0, 80),
        result_count: hits,
      })
    }, 800)
    return () => clearTimeout(timer)
  }, [query, records])

  const showingAll = category === 'all' && !deferredQuery.trim()
  // Searching or filtering is intent, so show everything that matched.
  const isBrowsing = showingAll && !expanded
  const visible = isBrowsing ? results.slice(0, INITIAL_ROWS) : results
  const hidden = results.length - visible.length

  return (
    <div>
      {/* Search */}
      <div className="rounded-2xl border border-forest/25 bg-white shadow-sm p-5 sm:p-6">
        <label htmlFor="product-search" className="block text-xs font-semibold uppercase tracking-[0.2em] text-forest">
          Check a product
        </label>
        <div className="relative mt-3">
          <svg
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            id="product-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Smartwater, tea bags, cutting board, air fryer…"
            autoComplete="off"
            className="w-full rounded-xl border border-ink/15 bg-cream/50 py-3.5 pl-12 pr-4 text-ink placeholder:text-gray-400 focus:border-forest focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40"
          />
        </div>
        <p className="mt-2.5 text-sm text-gray-500">
          Searches product names, brands, materials and the terms people
          actually type. No account, no install.
        </p>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCategory('all')}
          data-analytics-action="product_filter_all"
          aria-pressed={category === 'all'}
          className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
            category === 'all'
              ? 'border-forest bg-forest text-cream'
              : 'border-ink/15 bg-white text-gray-600 hover:border-forest/40 hover:text-ink'
          }`}
        >
          All {records.length}
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            type="button"
            onClick={() => setCategory(c.slug)}
            data-analytics-action={`product_filter_${c.slug}`}
            aria-pressed={category === c.slug}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              category === c.slug
                ? 'border-forest bg-forest text-cream'
                : 'border-ink/15 bg-white text-gray-600 hover:border-forest/40 hover:text-ink'
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Result count + sort */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 pb-3">
        <p className="text-sm text-gray-600" aria-live="polite">
          {showingAll ? (
            <>
              <strong className="text-ink tabular-nums">{results.length}</strong> products scored
            </>
          ) : (
            <>
              <strong className="text-ink tabular-nums">{results.length}</strong>{' '}
              {results.length === 1 ? 'match' : 'matches'}
            </>
          )}
        </p>
        <div className="flex items-center gap-1">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              data-analytics-action={`product_sort_${s.key}`}
              aria-pressed={sort === s.key}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                sort === s.key ? 'bg-ink/[0.07] text-ink' : 'text-gray-500 hover:text-ink'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {results.length > 0 ? (
        <>
        <ul className="mt-4 space-y-2.5">
          {visible.map((r) => (
            <li key={r.slug}>
              <Link
                href={`/products/${r.slug}`}
                onClick={() =>
                  track('product_result_click', {
                    cta_surface: 'product_explorer',
                    page_type: 'product_database',
                    product_slug: r.slug,
                  })
                }
                className="group flex items-center gap-4 rounded-xl border border-ink/10 bg-white p-4 transition-colors hover:border-forest/40"
              >
                {/* Mirrors ProductRow on the server-rendered lists. The bar
                    is what makes 30 rows scannable — a bare number forces the
                    reader to do every comparison in their head. */}
                <div className="flex w-12 shrink-0 flex-col items-center gap-1.5">
                  <span className="font-display text-lg font-bold leading-none tabular-nums text-ink">
                    {r.score}
                  </span>
                  <span className="h-1 w-full overflow-hidden rounded-full bg-ink/[0.08]">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${r.score}%`, backgroundColor: r.bandHex }}
                    />
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-ink transition-colors group-hover:text-forest">
                    {r.name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {r.categoryName} · {r.material}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <span
                    className={`inline-block rounded-full border px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider ${r.bandBadge}`}
                  >
                    {r.bandLabel}
                  </span>
                  <p className="mt-1 text-2xs tabular-nums text-gray-500">
                    Grade {r.grade} · {r.certainty}%
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>

          {hidden > 0 && (
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                data-analytics-action="product_show_all"
                className="inline-flex items-center rounded-xl border border-ink/15 bg-white px-6 py-3 text-sm font-medium text-ink transition-colors hover:border-forest/40 hover:text-forest"
              >
                Show all {results.length} products
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Or search — it covers every one of them.
              </p>
            </div>
          )}
        </>
      ) : (
        // The miss state is a conversion surface, not an error. The honest
        // thing and the commercial thing coincide here: we cannot answer for a
        // product we have not scored, and the app can.
        <div className="mt-6 rounded-2xl border border-forest/20 bg-gradient-to-br from-forest/10 to-forest/[0.03] p-6 text-center">
          <h2 className="font-display text-xl font-bold text-ink">
            We haven&apos;t scored “{query.trim()}” yet
          </h2>
          <p className="mx-auto mt-2 max-w-md text-gray-700 leading-relaxed">
            Fifty products is a start, not a database. Scan the item itself in
            the app and you&apos;ll get the same five-component score for the
            thing in your hand — and it goes on the list for a page here.
          </p>
          <AppStoreLink
            source="product_explorer_miss"
            ctaPosition="mid_article"
            trackingParams={{
              page_type: 'product_database',
              search_query: query.trim().toLowerCase().slice(0, 80),
            }}
            className="mt-5 inline-flex items-center rounded-control bg-ink px-6 py-3 text-cream transition-[color,background-color,border-color,text-decoration-color] hover:bg-ink/90 pressable"
          >
            Scan it in the app
          </AppStoreLink>
          <p className="mt-4 text-sm text-gray-600">
            Got the packet in your hand?{' '}
            <Link href="/check" className="text-forest underline">
              Check its barcode
            </Link>{' '}
            — that covers millions of products we have not written up.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Or clear the search to{' '}
            <button
              type="button"
              onClick={() => {
                // The action ID is static; the search text stays out of analytics.
                setQuery('')
                setCategory('all')
              }}
              className="text-forest underline"
              data-analytics-action="product_search_clear"
            >
              browse everything we have scored
            </button>
            .
          </p>
        </div>
      )}
    </div>
  )
}
